import { faClose, faExternalLink } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { FeatureLike } from "ol/Feature";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import type { EFASNotification } from '../../efas/notifications/Notification.types';

type PopupProps = {
    id: string;
    className: string;
    isPending: boolean;
    onClose: () => void;
    ref?: React.RefObject<HTMLDivElement | null>;
    feature?: FeatureLike;
    children?: React.ReactNode;
};

const NotificationPopup = ({ feature, onClose }: { feature: FeatureLike; onClose: () => void }) => {
    const forecastDate = moment.utc(feature.get("forecast_date"), 'YYYYMMDDHH').format("YYYY-MM-DD HH:mm");
    const sentOn = moment.utc(feature.get("sent_on")).format("YYYY-MM-DD HH:mm");

    const openNotificationPreview = (point_id: number, forecast_date: string) => {
        fetch(`/api/notifications/sent/?format=json&point_id=${point_id}&forecast_date=${forecast_date}`)
            .then(response => response.json())
            .then((data: EFASNotification[]) => {
                const notification = data[0];
                window.open(`/notifications-archive/detail/${notification.notification_id}`, "_blank");
            });
    }

    return (
        <div>
            <div role="button" style={{ float: 'right' }}
                onClick={(e) => { onClose(); e.stopPropagation(); e.preventDefault(); }}>
                <FontAwesomeIcon icon={faClose} />
            </div>
            <div
                className={`border rounded mx-3 text-center border-${feature.get("alert_type") === 1 ? "danger" : feature.get("alert_type") === 2 ? "warning" : "primary"}`}
            ><strong>{feature.get("alert_type") === 1 ? "Formal" : feature.get("alert_type") === 2 ? "Informal" : "Flash"} Flood Notification</strong></div>
            {feature.get("ad2_name") && <div>Region: <strong>{feature.get('ad2_name')}</strong></div>}
            <div>Country: <strong>{feature.get('country')}</strong></div>
            {feature.get("alert_type") !== 3 &&
                <>
                    <div>Basin Name: <strong>{feature.get('basin_name')}</strong></div>
                    <div>River Name: <strong>{feature.get('river_name')}</strong></div>
                </>}
            <div>Forecast: <strong>{forecastDate}</strong></div>
            <div>Sent On: <strong>{sentOn}</strong></div>
            <div>Sent By: <strong>{feature.get('user')}</strong></div>
            <div className="border text-center"
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    openNotificationPreview(feature.get("id"), feature.get("forecast_date"))
                }}>
                <span role="button" style={{ zIndex: 1000 }}>Open Notification details<br />(new tab)</span>
                <FontAwesomeIcon icon={faExternalLink} />
            </div>
        </div>
    );
};

const FeedbackPopup = ({ feature, onClose }: { feature: FeatureLike; onClose: () => void }) => {
    return (
        <div>
            <div role="button" style={{ float: 'right' }}
                onClick={(e) => { onClose(); e.stopPropagation(); e.preventDefault(); }}>
                <FontAwesomeIcon icon={faClose} />
            </div>
            <div><strong>Feedback</strong></div>
            <div>User: <strong>{feature.get("feedback_user")}</strong></div>
            {feature.get("flood_obs") && <div>Observed: <strong>{feature.get("flood_obs")}</strong></div>}
            {feature.get("added_value") && <div>Score: <strong>{feature.get("added_value")}</strong></div>}
            {feature.get("feedback_region") && <div>Region: <strong>{feature.get("feedback_region")}</strong></div>}
            {feature.get("river_name") && <div>River Name: <strong>{feature.get("river_name")}</strong></div>}
            {feature.get("basin_name") && <div>Basin Name: <strong>{feature.get("basin_name")}</strong></div>}
            {feature.get("country") && <div>Country: <strong>{feature.get("country")}</strong></div>}
            {feature.get("notification") && <Link className="btn-warning fs-7" target="_blank" to={`/feedbacks/${feature.get("notification")}/`}>View Feedback Details</Link>}
        </div>
    );
};

const MissedEventPopup = ({ feature, onClose }: { feature: FeatureLike; onClose: () => void }) => {
    return (
        <div>
            <div role="button" style={{ float: 'right' }}
                onClick={(e) => { onClose(); e.stopPropagation(); e.preventDefault(); }}>
                <FontAwesomeIcon icon={faClose} />
            </div>
            <div>River Name: <strong>{feature.get("rivername")}</strong></div>
            <div>Region Name: <strong>{feature.get("region_name")}</strong></div>
            <div>Country: <strong>{feature.get("country")}</strong></div>
            <div>Amount Rain: <strong>{feature.get("amount_rain")}</strong></div>
            <div>Impact: <strong>{feature.get("impact")}</strong></div>
            <div>Started Date: <strong>{feature.get("started_date")}</strong></div>
            <div>Ended Date: <strong>{feature.get("ended_date")}</strong></div>
        </div>
    );
};

export default function InteractiveMapPopup(props: PopupProps) {
    const activeNotificationFeature = props.feature?.get("forecast_date") || false;

    useEffect(() => {
        if (props.ref && props.ref.current && props.ref.current.parentElement) {
            props.ref.current.parentElement?.style.setProperty("pointer-events", "none");
        }
    // re-run when isPending flips, as that is when the ref'd div below is (un)mounted
    }, [props.ref, props.isPending]);

    return props.isPending && <div style={{ display: 'none' }}></div> || <div ref={props.ref} className={props.className}
        style={{
            transform: activeNotificationFeature ? 'translate(0%, -150%)' : 'translate(10%, -115%)',
            backgroundColor: 'white',
            padding: '5px',
            borderRadius: '5px',
            border: '1px solid black',
            minWidth: '200px',
            maxWidth: '300px',
            maxHeight: '400px',
            overflowY: 'auto',
            pointerEvents: 'all',
            // bottom: "-100px"
        }}
    >
        {props.feature &&
            <>
                {props.feature.get("feedback_user") ? (
                    <FeedbackPopup feature={props.feature} onClose={props.onClose} />
                ) : props.feature.get("forecast_date") ? (
                    <NotificationPopup feature={props.feature} onClose={props.onClose} />
                ) : (
                    <MissedEventPopup feature={props.feature} onClose={props.onClose} />
                )}
            </>
        }
    </div>
};